import axios from 'axios';
import IDataObject from '../models/restful/IDataObject';
import IResponse from '../models/responces/IResponse';
import ICollectionParams from '../models/params/ICollectionParams';
import { ICollectionResponse } from '../models/responces/ICollectionResponse';
import IMerchProductOrder from '../models/restful/IMerchProductOrder';
import IMerchProductOrderResponse from '../models/responces/IMerchProductOrderResponse';
import IMerchProductOrderItem from '../models/restful/IMerchProductOrderItem';
import { IFilesCollectionResponse } from '../models/responces/IFilesCollectionResponse';
import { AxiosRequestConfig } from 'axios';
import IUserResponse from '../models/responces/IUserResponse';
import IPayResponse from '../models/responces/IPayResponse';
import { useUser } from '../pages/orders/hooks/useUser';
import IFileResponse from '../models/responces/IFileResponse';
import ISubscriptionPlan from '../models/restful/ISubscriptionPlan';
import IDataObjectParams from '../models/params/IDataObjectParams';

const apiUrl = () => `${api.host}/api-v2`;
const url = (
  dataObjectName: string,
  id?: number | string,
  handler = 'crud',
  field = 'ID'
) => {
  if (field === 'ID')
    return `${apiUrl()}/${handler}/${dataObjectName}` + (id ? `/${id}` : ``);
  return `${apiUrl()}/${handler}/${dataObjectName}?${field}=${id}`;
};

const doParamsToString = (dataObjectParams: IDataObjectParams) => {
  const u = new URLSearchParams();
  for (const [key, value] of Object.entries(dataObjectParams)) {
    u.append(key, '' + value);
  }
  return u.toString();
};

const collectionParamsToString = (collectionParams: ICollectionParams) => {
  const u = new URLSearchParams();
  const buffer = [];
  for (const [key, value] of Object.entries(collectionParams)) {
    if (key === 'dataObjectParams') {
      const dataObjectParams = value as IDataObjectParams;
      buffer.push(doParamsToString(dataObjectParams));
    } else {
      u.append(key, '' + value);
    }
  }
  return `${u.toString()}&${buffer.join()}`;
};

export const api = {
  host: process.env.REACT_APP_API_URL,
  getToken: () => {
    const { getUser } = useUser();
    return getUser()?.RestfulToken;
  },
  signIn: (email: string, pass: string) =>
    axios({
      method: 'post',
      url: apiUrl() + '/auth/signin',
      data: { email: email, pass: pass },
      responseType: 'json'
    }).then(resp => resp.data) as Promise<IUserResponse>,
  signUp: (
    firstName: string,
    email: string,
    pass: string,
    lastName?: string,
    promoCode?: string
  ) =>
    axios({
      method: 'post',
      url: apiUrl() + '/auth/signup',
      data: {
        firstName: firstName,
        lastName: lastName,
        email: email,
        pass: pass,
        promoCode: promoCode
      },
      responseType: 'json'
    }).then(resp => resp.data) as Promise<IUserResponse>,
  create: (
    dataObjectName: string,
    dataObject: IDataObject,
    dataObjectParams: IDataObjectParams = {}
  ) =>
    axios({
      method: 'post',
      headers: { Authorization: api.getToken() },
      url: `${url(dataObjectName)}?${doParamsToString(dataObjectParams)}`,
      data: dataObject,
      responseType: 'json'
    }).then(resp => resp.data) as Promise<IResponse>,

  read: (
    dataObjectName: string,
    id: number | string,
    field = 'ID',
    dataObjectParams: IDataObjectParams = {}
  ) =>
    axios({
      method: 'get',
      headers: { Authorization: api.getToken() },
      url: `${url(dataObjectName, id, 'crud', field)}?${doParamsToString(
        dataObjectParams
      )}`,
      responseType: 'json'
    }).then(resp => resp.data) as Promise<IResponse>,

  update: (
    dataObjectName: string,
    dataObject: IDataObject,
    dataObjectParams: IDataObjectParams = {}
  ) =>
    axios({
      method: 'put',
      headers: { Authorization: api.getToken() },
      url: `${url(dataObjectName, dataObject.ID)}?${doParamsToString(
        dataObjectParams
      )}`,
      data: dataObject,
      responseType: 'json'
    }).then(resp => resp.data) as Promise<IResponse>,

  delete: (
    dataObjectName: string,
    ids: number[],
    collectionParams?: ICollectionParams
  ) =>
    axios({
      method: 'delete',
      headers: { Authorization: api.getToken() },
      url: url(dataObjectName, ids.join(',')),
      data: collectionParams ? collectionParams : {},
      responseType: 'json'
    }).then(resp => resp.data) as Promise<IResponse>,

  getCollection: (
    dataObjectName: string,
    collectionParams: ICollectionParams
  ) =>
    axios({
      method: 'get',
      headers: { Authorization: api.getToken() },
      url: `${url(dataObjectName)}?${collectionParamsToString(
        collectionParams
      )}`,
      responseType: 'json'
    }).then(resp => resp.data) as Promise<ICollectionResponse>,

  uploadFile: (file: File, cfg: AxiosRequestConfig = {}) => {
    const formData = new FormData();
    formData.append(`File`, file);
    return axios({
      ...cfg,
      method: 'post',
      headers: {
        Authorization: api.getToken(),
        'Content-Type': 'multipart/form-data'
      },
      url: url('File'),
      data: formData,
      responseType: 'json'
    }).then(resp => resp.data) as Promise<IFileResponse>;
  },
  upload: (
    dataObjectHolderName: string,
    dataObjectHolderID: number,
    relationName: string,
    files: File[],
    cfg: AxiosRequestConfig = {}
  ) => {
    const formData = new FormData();
    formData.append('relationName', relationName);
    files.forEach((file, idx) => formData.append(`File_${idx}`, file));
    return axios({
      ...cfg,
      method: 'post',
      headers: {
        Authorization: api.getToken(),
        'Content-Type': 'multipart/form-data'
      },
      url: url(dataObjectHolderName, dataObjectHolderID),
      data: formData,
      responseType: 'json'
    }).then(resp => resp.data) as Promise<IFilesCollectionResponse>;
  },

  createOrder: (
    order?: IMerchProductOrder,
    dataObjectParams: IDataObjectParams = {}
  ) =>
    axios({
      method: 'post',
      headers: { Authorization: api.getToken() },
      url: `${url('MerchProductOrder', 0, 'order/create')}?${doParamsToString(
        dataObjectParams
      )}`,
      data: order,
      responseType: 'json'
    }).then(resp => resp.data) as Promise<IMerchProductOrderResponse>,

  duplicateOrder: (id: number, dataObjectParams: IDataObjectParams = {}) =>
    axios({
      method: 'post',
      headers: { Authorization: api.getToken() },
      url: `${url(
        'MerchProductOrder',
        id,
        'order/duplicate'
      )}?${doParamsToString(dataObjectParams)}`,
      responseType: 'json'
    }).then(resp => resp.data) as Promise<IMerchProductOrderResponse>,

  createOrderItem: (
    orderItem?: IMerchProductOrderItem,
    dataObjectParams: IDataObjectParams = {}
  ) =>
    axios({
      method: 'post',
      headers: { Authorization: api.getToken() },
      url: `${url(
        'MerchProductOrderItem',
        0,
        'order/createOrderItem'
      )}?${doParamsToString(dataObjectParams)}`,
      data: orderItem,
      responseType: 'json'
    }).then(resp => resp.data) as Promise<IMerchProductOrderResponse>,

  deleteOrderItem: (orderItem?: IMerchProductOrderItem) =>
    axios({
      method: 'delete',
      headers: { Authorization: api.getToken() },
      url: url('MerchProductOrderItem', 0, 'order/deleteOrderItem'),
      data: orderItem,
      responseType: 'json'
    }).then(resp => resp.data) as Promise<IMerchProductOrderResponse>,

  confirm: (orders: IMerchProductOrder[]) =>
    axios({
      method: 'post',
      headers: { Authorization: api.getToken() },
      url: url(
        'MerchProductOrder',
        orders.map(o => o.ID).join(','),
        'order/confirm'
      ),
      // data: ,
      responseType: 'json'
    }).then(resp => resp.data) as Promise<IResponse>,

  pay: (orders: IMerchProductOrder[]) =>
    axios({
      method: 'post',
      headers: { Authorization: api.getToken() },
      url: url(
        'MerchProductOrder',
        orders.map(o => o.ID).join(','),
        'order/pay'
      ),
      // data: ,
      responseType: 'json'
    }).then(resp => resp.data) as Promise<IPayResponse>,
  payPlan: (plan: ISubscriptionPlan, options: { period: 'month' | 'year' }) =>
    axios({
      method: 'post',
      headers: { Authorization: api.getToken() },
      url: url('pay', '', 'subscription'),
      data: { ...plan, _options: options },
      responseType: 'json'
    }).then(resp => resp.data) as Promise<IPayResponse>,

  transaction: (data: { hash: string; success: boolean }) =>
    axios({
      method: 'put',
      headers: { Authorization: api.getToken() },
      url: url('Transaction', 0, 'order/transaction'),
      data: data,
      responseType: 'json'
    }).then(resp => resp.data) as Promise<IResponse>,

  updateOrders: (orderItem?: IMerchProductOrderItem) =>
    axios({
      method: 'post',
      headers: { Authorization: api.getToken() },
      url: url('MerchProductOrderItem', 0, 'order/updateOrders'),
      data: orderItem,
      responseType: 'json'
    }).then(resp => resp.data) as Promise<IResponse>
};
